import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X } from "@phosphor-icons/react";
import { StatusBadge } from "@/components/StatusBadge";

/**
 * Lazy content preview modal.
 *
 * Fetches `/api/campaigns/{campaignId}/queue/{rowId}/content` only when
 * opened, so the parent list stays lightweight. Shows the exact
 * subject/text/html that was rendered for that sender→recipient pair,
 * plus status/error info.
 */
export default function ContentPreviewModal({ campaignId, rowId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [c, setC] = useState(null);
  const [view, setView] = useState("text");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/campaigns/${campaignId}/queue/${rowId}/content`);
        if (alive) setC(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed to fetch content");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [campaignId, rowId]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="content-preview-modal"
    >
      <div
        className="w-full max-w-3xl bg-[#0A0A0A] border border-[#2A2A2A] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#2A2A2A] flex items-start justify-between">
          <div>
            <div className="micro mb-1">Sent Content</div>
            <h3 className="font-heading text-xl font-semibold text-white">
              Preview
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="content-preview-close"
            className="w-8 h-8 flex items-center justify-center border border-[#2A2A2A] hover:border-[#E1FF00] hover:text-[#E1FF00] text-[#A1A1AA]"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="font-mono text-[#71717A] text-sm py-8 text-center" data-testid="content-preview-loading">
              [ loading content... ]
            </div>
          )}
          {!loading && error && (
            <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 font-mono text-[11px] text-[#FF3B30]" data-testid="content-preview-error">
              {error}
            </div>
          )}
          {!loading && !error && c && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                <MetaRow label="From" v={c.sender_email} />
                <MetaRow label="To" v={c.recipient_email} />
                <MetaRow label="Batch" v={`B${c.batch_number || 0}`} />
                <MetaRow label="Cycle" v={c.sender_cycle_number || 0} />
                <MetaRow label="Attempts" v={c.attempt_count || 0} />
                <MetaRow label="Processed at" v={c.processed_at?.slice(0, 19) || "—"} />
                <div className="col-span-2 flex items-center gap-2">
                  <span className="micro">Status</span>
                  <StatusBadge status={c.status} />
                </div>
              </div>

              <div className="border-t border-[#2A2A2A] pt-3">
                <div className="micro mb-2">Subject</div>
                <div
                  className="font-mono text-sm text-white bg-[#0F0F0F] border border-[#2A2A2A] p-3 break-words"
                  data-testid="content-preview-subject"
                >
                  {c.subject || <span className="text-[#71717A]">[ not rendered yet ]</span>}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="micro">Body</div>
                  {c.html_body && (
                    <div className="flex border border-[#2A2A2A]">
                      <button
                        onClick={() => setView("text")}
                        data-testid="content-preview-tab-text"
                        className={`px-3 py-1 text-[10px] uppercase font-mono ${view === "text" ? "bg-[#E1FF00] text-black" : "text-[#A1A1AA]"}`}
                      >
                        Text
                      </button>
                      <button
                        onClick={() => setView("html")}
                        data-testid="content-preview-tab-html"
                        className={`px-3 py-1 text-[10px] uppercase font-mono ${view === "html" ? "bg-[#E1FF00] text-black" : "text-[#A1A1AA]"}`}
                      >
                        HTML
                      </button>
                    </div>
                  )}
                </div>
                {c.text_body || c.html_body ? (
                  view === "html" && c.html_body ? (
                    <iframe
                      title="html-preview"
                      srcDoc={c.html_body}
                      sandbox=""
                      className="w-full min-h-[280px] bg-white border border-[#2A2A2A]"
                      data-testid="content-preview-html"
                    />
                  ) : (
                    <pre
                      className="whitespace-pre-wrap font-mono text-xs text-[#E4E4E7] bg-[#0F0F0F] border border-[#2A2A2A] p-3 max-h-[420px] overflow-y-auto"
                      data-testid="content-preview-body"
                    >{c.text_body || "[ no text body captured ]"}</pre>
                  )
                ) : (
                  <div className="font-mono text-[11px] text-[#71717A] italic border border-[#2A2A2A] p-3" data-testid="content-preview-empty">
                    Content hasn&apos;t been rendered yet — this row is still pending in the queue.
                  </div>
                )}
              </div>

              {c.last_error && (
                <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3">
                  <div className="micro text-[#FF3B30] mb-1">Last error</div>
                  <div className="font-mono text-[11px] text-[#FF3B30] break-words">
                    {c.last_error}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, v }) {
  return (
    <div>
      <div className="micro mb-1">{label}</div>
      <div className="font-mono text-xs text-white truncate">{v ?? "—"}</div>
    </div>
  );
}
